import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, ExternalLink } from 'lucide-react';

const MobileDetection = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // 检测移动设备
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileDevice = mobileRegex.test(userAgent);
      
      // 检测屏幕尺寸
      const isSmallScreen = window.innerWidth <= 768;
      
      // 检测触摸设备
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      const mobile = isMobileDevice || (isSmallScreen && isTouchDevice);
      
      setIsMobile(mobile);
      setShowMobileWarning(mobile);
    };

    checkMobile();
    
    // 监听屏幕尺寸变化
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleContinueAnyway = () => {
    setShowMobileWarning(false);
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText('https://kcb.aiqji.com/').then(() => {
      alert('网址已复制到剪贴板！');
    }).catch(() => {
      // 如果复制失败，显示网址让用户手动复制
      prompt('请复制以下网址：', 'https://kcb.aiqji.com/');
    });
  };

  if (!isMobile || !showMobileWarning) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        {/* 图标 */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Monitor className="w-16 h-16 text-blue-600" />
            <div className="absolute -top-2 -right-2 bg-orange-500 rounded-full p-1">
              <Smartphone className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        {/* 标题 */}
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          请使用电脑访问
        </h1>

        {/* 说明 */}
        <div className="text-gray-600 mb-6 space-y-3">
          <p className="text-lg">
            由于工具特殊性，建议您使用电脑访问以获得最佳体验。
          </p>
          <div className="bg-blue-50 rounded-lg p-4 text-sm">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>支持拖拽操作制作课程表</div>
            </div>
            <div className="flex items-start space-x-2 mt-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>更大屏幕显示完整课程表</div>
            </div>
            <div className="flex items-start space-x-2 mt-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <div>更好的编辑和导出体验</div>
            </div>
          </div>
        </div>

        {/* 网址 */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-500 mb-2">电脑访问网址：</p>
          <div className="flex items-center justify-between bg-white rounded border p-3">
            <span className="text-blue-600 font-mono text-sm">
              https://kcb.aiqji.com/
            </span>
            <button
              onClick={handleCopyUrl}
              className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
              title="复制网址"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 按钮 */}
        <div className="space-y-3">
          <button
            onClick={handleCopyUrl}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <ExternalLink className="w-4 h-4" />
            <span>复制网址到电脑打开</span>
          </button>
          
          <button
            onClick={handleContinueAnyway}
            className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            仍要继续（无法正常使用）
          </button>
        </div>

        {/* 底部说明 */}
        <p className="text-xs text-gray-400 mt-6">
          智能课程表生成器 - 专业的在线课程表制作工具
        </p>
      </div>
    </div>
  );
};

export default MobileDetection;
